import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import { 
  Add as AddIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useCompany } from '../context/CompanyContext';

const StockCounting = () => {
  const { stockItems } = useCompany();
  const [countingSessions, setCountingSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // TODO: Load counting sessions from API
    setCountingSessions([
      {
        id: 1,
        name: 'Ocak 2024 Sayımı',
        status: 'completed',
        startDate: '2024-01-15',
        endDate: '2024-01-20',
        totalItems: 1500,
        countedItems: 1500,
        differences: 25
      },
      {
        id: 2,
        name: 'Şubat 2024 Sayımı',
        status: 'in_progress',
        startDate: '2024-02-01',
        endDate: null,
        totalItems: 1600,
        countedItems: 800,
        differences: 12
      }
    ]);
  }, []);

  const handleStartCounting = () => {
    setOpenDialog(true);
  };

  const handleCreateSession = (sessionData) => {
    // TODO: Create new counting session
    console.log('Create session:', sessionData);
    setOpenDialog(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in_progress':
        return <ErrorIcon color="warning" />;
      default:
        return <ErrorIcon />;
    }
  };

  const calculateProgress = (session) => {
    return (session.countedItems / session.totalItems) * 100;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Stok Sayımı
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleStartCounting}
        >
          Yeni Sayım Başlat
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Counting Sessions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sayım Oturumları
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sayım Adı</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Başlangıç</TableCell>
                      <TableCell>Bitiş</TableCell>
                      <TableCell>İlerleme</TableCell>
                      <TableCell>Farklar</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {countingSessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>{session.name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(session.status)}
                            <Chip 
                              label={session.status === 'completed' ? 'Tamamlandı' : 
                                     session.status === 'in_progress' ? 'Devam Ediyor' : 'Bekliyor'} 
                              color={getStatusColor(session.status)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{session.startDate}</TableCell>
                        <TableCell>{session.endDate || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={calculateProgress(session)} 
                              sx={{ width: 100 }}
                            />
                            <Typography variant="body2">
                              {Math.round(calculateProgress(session))}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${session.differences} fark`} 
                            color={session.differences > 0 ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton>
                              <SearchIcon />
                            </IconButton>
                            <IconButton>
                              <PrintIcon />
                            </IconButton>
                            <IconButton>
                              <DownloadIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hızlı İşlemler
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SearchIcon />}
                  >
                    Ürün Ara
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PrintIcon />}
                  >
                    Rapor Yazdır
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                  >
                    Excel İndir
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                  >
                    Farkları Onayla
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sayım İstatistikleri
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {countingSessions.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Sayım
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {countingSessions.filter(s => s.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tamamlanan
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {countingSessions.filter(s => s.status === 'in_progress').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Devam Eden
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {countingSessions.reduce((sum, s) => sum + s.differences, 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Fark
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* New Counting Session Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Sayım Oturumu Başlat</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sayım Adı"
                placeholder="Örn: Ocak 2024 Stok Sayımı"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Açıklama"
                multiline
                rows={3}
                placeholder="Sayım hakkında açıklama..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Başlangıç Tarihi"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bitiş Tarihi"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Sayım Türü</InputLabel>
                <Select label="Sayım Türü">
                  <MenuItem value="full">Tam Sayım</MenuItem>
                  <MenuItem value="partial">Kısmi Sayım</MenuItem>
                  <MenuItem value="cycle">Döngüsel Sayım</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>İptal</Button>
          <Button variant="contained">Başlat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockCounting;
